import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { TextField } from 'src/components/TextField';
import { fetchAllGroupsByPluckColumns } from 'src/models/group/group';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';

import { FilterTypeComponentProps } from './types';

export const FilterTypeGroup: FC<FilterTypeComponentProps> = ({
  value,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'reminderFilters.filterSet',
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups', 'options'],
    queryFn: () => fetchAllGroupsByPluckColumns('id,name'),
    staleTime: 60000,
    gcTime: 60000,
  });

  const [editing, setEditing] = useState(false);

  const handleToggle = () => setEditing((e) => !e);

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {editing ? (
        <>
          {t('are')}
          <TextField
            size="medium"
            value={value.negate ? 'not' : 'is'}
            onChange={(e) =>
              onChange({ ...value, negate: e.target.value === 'not' })
            }
            options={[
              { value: 'is', label: t('aMember') },
              { value: 'not', label: t('notAMember') },
            ]}
            sx={{ minWidth: 100 }}
          />
          {t('of')}
          <BaseSelect
            value={value.value ?? ''}
            onChange={(v) => onChange({ ...value, value: v || null })}
            options={groups.map((g) => ({
              id: g.id.toString(),
              label: g.name,
            }))}
            placeholder={t('anyGroup')}
            sx={{ minWidth: 200 }}
          />
          <Button color="primary" variant="prominent" onClick={handleToggle}>
            {t('done')}
          </Button>
        </>
      ) : (
        <>
          {t('are')} {value.negate && t('not')}{' '}
          {value.value
            ? `${t('memberOf')} ${
                groups.find((g) => g.id == value.value)?.name
              }`
            : t('memberOfAnyGroup')}
          <IconButton size="small" color="primary" onClick={handleToggle}>
            <Icon icon={faEdit} size="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete()}>
            <Icon icon={faTrash} size="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};
